/**
 * Survey Analysis Page
 *
 * Page for AI analysis of survey responses
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Sparkles,
  Play,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  TrendingUp,
  Info,
  CheckCircle,
  FileText,
  BarChart3,
  Download,
} from 'lucide-react';
import Button from '../../components/Button';
import Layout from '../../components/Layout';
import {
  getSurveyById,
  triggerSurveyAnalysis,
  getSurveyAnalysis,
  pollSurveyAnalysisCompletion,
  SurveyAnalysisResult,
  QuestionAnalysis,
} from '../../utils/surveyApi';
import AnalysisMarkdown from '../../components/common/AnalysisMarkdown';
import { downloadAnalysisAsMarkdown } from '../../utils/exportAnalysis';

const SurveyAnalysisPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const surveyId = parseInt(id || '0', 10);

  const [survey, setSurvey] = useState<any>(null);
  const [analysis, setAnalysis] = useState<SurveyAnalysisResult | null>(null);
  const [isLoadingSurvey, setIsLoadingSurvey] = useState(true);
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [analysisGoal, setAnalysisGoal] = useState('');
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());
  const [showSynthesis, setShowSynthesis] = useState(true);
  const [showQuestions, setShowQuestions] = useState(true);

  useEffect(() => {
    if (surveyId) {
      loadSurvey();
      loadExistingAnalysis();
    }
  }, [surveyId]);

  const loadSurvey = async () => {
    setIsLoadingSurvey(true);
    try {
      const result = await getSurveyById(surveyId);
      setSurvey(result.survey);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoadingSurvey(false);
    }
  };

  const loadExistingAnalysis = async () => {
    setIsLoadingAnalysis(true);
    try {
      const result = await getSurveyAnalysis(surveyId);
      setAnalysis(result);

      if (result.status === 'processing' || result.status === 'pending') {
        pollForCompletion(result.analysis_id);
      }
    } catch (err: any) {
      if (!err.message?.includes('404')) {
        console.error('Error loading analysis:', err);
      }
    } finally {
      setIsLoadingAnalysis(false);
    }
  };

  const pollForCompletion = async (analysisId: number) => {
    setIsRunning(true);
    try {
      const result = await pollSurveyAnalysisCompletion(surveyId, analysisId, {
        timeout: 600000,
        pollInterval: 3000,
        onProgress: (prog, msg) => {
          setProgress(prog);
          setProgressMessage(msg);
        },
      });
      setAnalysis(result);
      setProgress(100);
      setProgressMessage('Analysis complete');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsRunning(false);
    }
  };

  const handleRunAnalysis = async () => {
    setError(null);
    setIsRunning(true);
    setProgress(0);
    setProgressMessage('Starting analysis...');

    try {
      const { analysis_id } = await triggerSurveyAnalysis(surveyId, analysisGoal || undefined);
      await pollForCompletion(analysis_id);
    } catch (err: any) {
      setError(err.message);
      setIsRunning(false);
    }
  };

  const toggleQuestion = (questionId: string) => {
    setExpandedQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) {
        next.delete(questionId);
      } else {
        next.add(questionId);
      }
      return next;
    });
  };

  const renderQuestionAnalysis = (qa: QuestionAnalysis) => {
    const isExpanded = expandedQuestions.has(qa.question_id);

    return (
      <div key={qa.question_id} className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleQuestion(qa.question_id)}
          className="w-full px-4 py-3 flex items-start justify-between bg-gray-50 hover:bg-gray-100 transition-colors text-left"
        >
          <div className="flex-1 pr-4">
            <p className="text-sm font-medium text-gray-900">{qa.question_text}</p>
            {qa.tldr && (
              <div className="text-sm text-gray-600 mt-1">
                <AnalysisMarkdown>{qa.tldr}</AnalysisMarkdown>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
              {qa.total_responses} responses
            </span>
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </button>

        {isExpanded && (
          <div className="p-4 space-y-4 bg-white">
            {qa.synthesis && (
              <div className="flex items-start space-x-3">
                <TrendingUp className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-2">
                    Strategic Insight
                  </p>
                  <AnalysisMarkdown>{qa.synthesis}</AnalysisMarkdown>
                </div>
              </div>
            )}

            {qa.counter_arguments && qa.counter_arguments.length > 0 && (
              <div className="flex items-start space-x-3">
                <Info className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2">
                    Worth Noting
                  </p>
                  {qa.counter_arguments.map((arg, idx) => (
                    <AnalysisMarkdown key={idx}>{arg}</AnalysisMarkdown>
                  ))}
                </div>
              </div>
            )}

            {qa.distribution && qa.distribution.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Response Distribution
                </p>
                <div className="space-y-2">
                  {qa.type === 'percentage_allocation'
                    ? // Percentage allocation: show active/primary/loyalist bars per option
                      qa.distribution.map((item: any, idx: number) => (
                        <div key={idx} className="mb-3">
                          <div className="text-xs font-medium text-gray-700 mb-1">
                            {item.choice}
                          </div>
                          <div className="space-y-1">
                            {[
                              { label: 'Active (>0%)', data: item.active, color: 'bg-[#07E5D1]' },
                              {
                                label: 'Primary (≥50%)',
                                data: item.primary,
                                color: 'bg-[#06B6D4]',
                              },
                              {
                                label: 'Loyalist (≥75%)',
                                data: item.loyalist,
                                color: 'bg-[#0891B2]',
                              },
                            ].map((tier) => (
                              <div key={tier.label} className="flex items-center">
                                <div className="w-28 text-xs text-gray-500 truncate">
                                  {tier.label}
                                </div>
                                <div className="flex-1 mx-2">
                                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                      className={`h-full ${tier.color} rounded-full`}
                                      style={{ width: `${tier.data?.percentage ?? 0}%` }}
                                    />
                                  </div>
                                </div>
                                <div className="w-20 text-xs text-gray-500 text-right">
                                  {tier.data?.count ?? 0} (
                                  {tier.data?.percentage?.toFixed(1) ?? '0'}%)
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    : // Standard distribution: single bar per option
                      qa.distribution
                        .filter((item: any) => item.count > 0)
                        .slice(0, 10)
                        .map((item: any, idx: number) => (
                          <div key={idx} className="flex items-center">
                            <div
                              className="w-32 text-xs text-gray-600 truncate"
                              title={String(
                                item.choice ??
                                  item.rating ??
                                  item.level ??
                                  item.score ??
                                  item.value ??
                                  '',
                              )}
                            >
                              {item.choice ?? item.rating ?? item.level ?? item.score ?? item.value}
                            </div>
                            <div className="flex-1 mx-2">
                              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-[#07E5D1] rounded-full"
                                  style={{ width: `${item.percentage}%` }}
                                />
                              </div>
                            </div>
                            <div className="w-16 text-xs text-gray-500 text-right">
                              {item.percentage?.toFixed(1) ?? '0'}%
                            </div>
                          </div>
                        ))}
                </div>
              </div>
            )}

            {/* Yes/No Results */}
            {qa.yes_count !== undefined && qa.no_count !== undefined && (
              <div className="mt-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Response Distribution
                </p>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <div className="w-32 text-xs text-gray-600">Yes</div>
                    <div className="flex-1 mx-2">
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#07E5D1] rounded-full"
                          style={{ width: `${qa.yes_percentage ?? 0}%` }}
                        />
                      </div>
                    </div>
                    <div className="w-16 text-xs text-gray-500 text-right">
                      {qa.yes_percentage?.toFixed(1) ?? '0'}%
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="w-32 text-xs text-gray-600">No</div>
                    <div className="flex-1 mx-2">
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gray-400 rounded-full"
                          style={{ width: `${qa.no_percentage ?? 0}%` }}
                        />
                      </div>
                    </div>
                    <div className="w-16 text-xs text-gray-500 text-right">
                      {qa.no_percentage?.toFixed(1) ?? '0'}%
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Rankings (Ranking & MaxDiff) */}
            {qa.rankings && qa.rankings.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Rankings
                </p>
                <div className="space-y-2">
                  {qa.rankings.slice(0, 10).map((item: any, idx: number) => (
                    <div key={idx} className="flex items-center justify-between text-xs">
                      <span
                        className="text-gray-700 truncate flex-1"
                        title={item.item || item.option}
                      >
                        {idx + 1}. {item.item || item.option}
                      </span>
                      {item.average_rank !== undefined && (
                        <span className="text-gray-500 ml-2">Avg: {item.average_rank}</span>
                      )}
                      {item.net_score !== undefined && (
                        <span
                          className={`ml-2 ${item.net_score >= 0 ? 'text-green-600' : 'text-red-600'}`}
                        >
                          Net: {item.net_score > 0 ? '+' : ''}
                          {item.net_score}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Matrix Items */}
            {qa.items && qa.items.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Item Ratings
                </p>
                <div className="space-y-2">
                  {qa.items.slice(0, 10).map((item: any, idx: number) => (
                    <div key={idx} className="flex items-center">
                      <div className="w-32 text-xs text-gray-600 truncate" title={item.item}>
                        {item.item}
                      </div>
                      <div className="flex-1 mx-2">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#07E5D1] rounded-full"
                            style={{ width: `${(item.average / 5) * 100}%` }}
                          />
                        </div>
                      </div>
                      <div className="w-16 text-xs text-gray-500 text-right">
                        {item.average?.toFixed(1) ?? '0'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Conjoint Preferences */}
            {qa.preferences && qa.preferences.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Preferences
                </p>
                <div className="space-y-2">
                  {qa.preferences
                    .filter((item: any) => item.count > 0)
                    .slice(0, 10)
                    .map((item: any, idx: number) => (
                      <div key={idx} className="flex items-center">
                        <div className="w-32 text-xs text-gray-600 truncate" title={item.choice}>
                          {item.choice}
                        </div>
                        <div className="flex-1 mx-2">
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#07E5D1] rounded-full"
                              style={{ width: `${item.percentage}%` }}
                            />
                          </div>
                        </div>
                        <div className="w-16 text-xs text-gray-500 text-right">
                          {item.percentage?.toFixed(1) ?? '0'}%
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Image Hotspot Products */}
            {qa.products && qa.products.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Product Clicks
                </p>
                <div className="space-y-2">
                  {qa.products.slice(0, 10).map((item: any, idx: number) => (
                    <div key={idx} className="flex items-center justify-between text-xs">
                      <span className="text-gray-700 truncate flex-1" title={item.product}>
                        {idx + 1}. {item.product}
                      </span>
                      <span className="text-gray-500 ml-2">
                        {item.first_choice_count} first picks, {item.total_clicks} total
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (isLoadingSurvey) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 py-8">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading survey...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!survey) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 py-8">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-white rounded-lg shadow-sm p-6 text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Survey not found</h3>
              <Button variant="primary" onClick={() => navigate('/surveys')}>
                Back to Surveys
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const responsesCount = survey.responses_count || 0;

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate(`/surveys/${surveyId}`)}
              icon={<ArrowLeft className="w-4 h-4 mr-1" />}
            >
              Back to Survey
            </Button>
            {analysis?.status === 'completed' && (
              <Button
                variant="outline"
                size="sm"
                icon={<Download className="w-4 h-4" />}
                onClick={() => downloadAnalysisAsMarkdown(analysis, survey?.name)}
              >
                Export
              </Button>
            )}
          </div>

          {/* Page Title */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-center mb-2">
              <Sparkles className="w-6 h-6 text-[#07E5D1] mr-2" />
              <h1 className="text-2xl font-bold text-gray-900">AI Analysis</h1>
            </div>
            <p className="text-gray-600">{survey.name}</p>
            <div className="flex items-center mt-4 space-x-4 text-sm text-gray-500">
              <span className="flex items-center">
                <FileText className="w-4 h-4 mr-1" />
                {survey.questions?.length || 0} questions
              </span>
              <span className="flex items-center">
                <BarChart3 className="w-4 h-4 mr-1" />
                {responsesCount} responses
              </span>
              {analysis?.status === 'completed' && (
                <span className="flex items-center text-green-600">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Analysis available
                </span>
              )}
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* No Responses State */}
          {responsesCount === 0 && (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No responses yet</h3>
              <p className="text-gray-500 mb-4">Collect survey responses to enable AI analysis</p>
              <Button variant="outline" onClick={() => navigate(`/surveys/${surveyId}`)}>
                Back to Survey
              </Button>
            </div>
          )}

          {/* Run Analysis Section - shown first if no analysis exists */}
          {responsesCount > 0 && !isRunning && !analysis && (
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Run Analysis</h2>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Analysis Goal
                </label>
                <textarea
                  value={analysisGoal}
                  onChange={(e) => setAnalysisGoal(e.target.value)}
                  placeholder="e.g., What flavor preferences drive purchase intent? Which claims resonate most with consumers?"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-[#07E5D1] focus:border-[#07E5D1]"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Provide specific business questions to focus the analysis
                </p>
              </div>

              <Button
                variant="primary"
                onClick={handleRunAnalysis}
                disabled={isLoadingAnalysis}
                icon={<Play className="w-4 h-4" />}
                className="bg-[#07E5D1] hover:bg-[#06D4C2]"
              >
                Run Analysis
              </Button>
            </div>
          )}

          {/* Progress Display */}
          {isRunning && (
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="flex items-center mb-3">
                <Loader2 className="w-5 h-5 text-[#07E5D1] animate-spin mr-3" />
                <span className="font-medium text-gray-900">Analysis in progress...</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div
                  className="bg-[#07E5D1] h-3 rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-sm text-gray-600">{progressMessage || `${progress}% complete`}</p>
            </div>
          )}

          {/* Loading Analysis State */}
          {isLoadingAnalysis && !isRunning && (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <Loader2 className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-500">Loading analysis...</p>
            </div>
          )}

          {/* Analysis Results */}
          {analysis?.status === 'completed' && !isLoadingAnalysis && !isRunning && (
            <div className="space-y-6">
              {/* Analysis Goal */}
              {analysis.analysis_goal && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Analysis Goal</h2>
                  <p className="text-sm text-gray-700">{analysis.analysis_goal}</p>
                </div>
              )}

              {/* Survey Synthesis */}
              {analysis.survey_synthesis?.markdown && (
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  <button
                    onClick={() => setShowSynthesis(!showSynthesis)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-100 transition-colors"
                  >
                    <h2 className="text-2xl font-bold text-gray-900">Overall Synthesis</h2>
                    {showSynthesis ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </button>

                  {showSynthesis && (
                    <div className="px-6 pb-6">
                      <AnalysisMarkdown>{analysis.survey_synthesis.markdown}</AnalysisMarkdown>
                    </div>
                  )}
                </div>
              )}

              {/* Question-by-Question Analysis */}
              {analysis.question_analyses && Object.keys(analysis.question_analyses).length > 0 && (
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  <button
                    onClick={() => setShowQuestions(!showQuestions)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-100 transition-colors"
                  >
                    <h2 className="text-2xl font-bold text-gray-900">
                      Question-by-Question Analysis
                    </h2>
                    {showQuestions ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </button>

                  {showQuestions && (
                    <div className="px-6 pt-2 pb-6 space-y-3">
                      {Object.values(analysis.question_analyses).map((qa) =>
                        renderQuestionAnalysis(qa),
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Re-run Analysis Section */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Re-run Analysis</h2>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Analysis Goal
                  </label>
                  <textarea
                    value={analysisGoal}
                    onChange={(e) => setAnalysisGoal(e.target.value)}
                    placeholder="e.g., What flavor preferences drive purchase intent? Which claims resonate most with consumers?"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-[#07E5D1] focus:border-[#07E5D1]"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Provide specific business questions to focus the analysis
                  </p>
                </div>

                <Button
                  variant="primary"
                  onClick={handleRunAnalysis}
                  disabled={isLoadingAnalysis}
                  icon={<Play className="w-4 h-4" />}
                  className="bg-[#07E5D1] hover:bg-[#06D4C2]"
                >
                  Re-run Analysis
                </Button>

                <p className="text-xs text-gray-500 mt-2">
                  Last analysis: {analysis.responses_analyzed} responses analyzed
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default SurveyAnalysisPage;
